import { EquipmentItem } from '@/app/models/detailsEquipment';
import { FinancialGroup, SubGroup } from '@/app/models/financalGroup';
import { TransferDetailBase } from '@/app/models/transferDetail';
import { useSearchEquipmentQuery, useUnitDetailsEquipmentMutation } from '@/app/services/detailsEquipmentApi';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import LateralDrawer from '@/components/common/LateralDrawer';
import { useFinancialGroup } from '@/context/FinancialGroup';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Button, FormControl, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GenericAutoComplete from '../../common/GenericAutoComplete';
import NewEditTransferCost from '../NewEditTransferCost';
import EquipmentTable from './EquipmentTable';
import NewEditEquipmentDetails from './NewEditEquipmentDetails';

interface EquipmentProps {
  group: FinancialGroup;
}

function Equipment(props: EquipmentProps) {
  const theme = useTheme();
  const { code } = useParams();
  const period = usePeriod();
  const { t } = useTranslation(['equipment', 'common']);

  const { group } = props;

  const [drawerEdit, setDrawerEdit] = useState(false);
  const [drawerTransfer, setDrawerTransfer] = useState(false);

  const { data: financialsubGroup } = useFinancialSubGroupQuery({ groupCode: group?.id }, { skip: !group });
  const { selectedFinancialSubGroup, setSelectedFinancialSubGroup } = useFinancialSubGroup();

  useEffect(() => {
    if (financialsubGroup && financialsubGroup.length > 0) {
      const found = financialsubGroup.find((item) => item.name === group.name) ?? financialsubGroup[0];
      setSelectedFinancialSubGroup(found);
    }
  }, [financialsubGroup, group, setSelectedFinancialSubGroup]);

  const [selectEquipment, setSelectEquipment] = useState<number[]>([]);

  const [originSupplier, setOriginSupplier] = useState<EquipmentItem | null>(null);
  const [originSupplierSearch, setOriginSupplierSearch] = useState('');

  const { data: equipment } = useSearchEquipmentQuery(
    {
      period: period.selectedPeriod?.date as string,
      project: code || '',
      subGroup: selectedFinancialSubGroup?.id as number,
      searchTerm: originSupplierSearch || '',
    },
    {
      skip: !period.selectedPeriod?.date || !code || !selectedFinancialSubGroup,
    },
  );

  const [selectEquipamentItem, setSelectedEquipamentItem] = useState<EquipmentItem>();

  const [unit] = useUnitDetailsEquipmentMutation();

  const handleUnite = async () => {
    await toast.promise(
      unit({
        ids: selectEquipment,
        period: period.selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setSelectEquipment([]);
  };

  const [selectedItem, setSelectedItem] = useState<TransferDetailBase | undefined>();

  const { selectedFinancialGroup } = useFinancialGroup();

  useEffect(() => {
    if (!selectEquipamentItem || !selectedFinancialSubGroup || !period.selectedPeriod) return;

    setSelectedItem({
      id: selectEquipamentItem.id,
      originFinancialGroupCode: selectedFinancialGroup?.code as string,
      financialSubGroupId: selectedFinancialSubGroup.id,
      period: period.selectedPeriod.date,
      complement: selectEquipamentItem.complement,
      newDetail: {
        description: selectEquipamentItem.description,
        value: selectEquipamentItem.value,
        supplier: selectEquipamentItem.description || selectEquipamentItem.supplier || '',
      },
    });
  }, [selectedFinancialSubGroup, period.selectedPeriod, selectedFinancialGroup, selectEquipamentItem]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ minWidth: theme.spacing(31) }}>
              <Typography component="p" variant="fieldHeader">
                {t('common:group')}
              </Typography>
              <FormControl sx={{ width: theme.spacing(30) }}>
                <Select
                  displayEmpty
                  value={selectedFinancialSubGroup?.id || ({} as SubGroup)}
                  onChange={(e) => {
                    const selected = financialsubGroup?.find((item) => item.id === e.target.value);
                    if (selected) setSelectedFinancialSubGroup(selected);
                  }}
                  sx={{ height: 32, fontSize: 14 }}
                >
                  {financialsubGroup &&
                    financialsubGroup.map((item: SubGroup) => (
                      <MenuItem key={item.id} value={item.id} sx={{ fontSize: 13 }}>
                        {`${item.name}`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box sx={{ minWidth: theme.spacing(31), display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography component="p" variant="fieldHeader">
              {t('common:search')}
            </Typography>
            <GenericAutoComplete
              value={originSupplier}
              onChange={(value) => {
                setOriginSupplier(value);
              }}
              equalityTest={(option, value) =>
                option?.supplier === value?.supplier || option?.description === value?.description
              }
              getOptionLabel={(option) => (option ? `${option.description ?? ''}` : '')}
              onSearch={(value) => {
                setOriginSupplierSearch(value);
              }}
              options={[]}
              placeholder={t('common:type.to.search') as string}
              sx={{ width: theme.spacing(80) }}
            />
          </Box>
          <Box sx={{ minWidth: theme.spacing(31) }}>
            <Typography variant="fieldHeader">
              {t('common:total.by.group')}
              <Typography>{formatterMonetaryValue(equipment?.header.totalValue || null)}</Typography>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Stack direction="row" alignItems="center">
            <Button
              variant="primary"
              sx={{ width: theme.spacing(20) }}
              onClick={() => handleUnite()}
              disabled={selectEquipment.length === 0}
            >
              {t('common:unite')}
            </Button>
          </Stack>
        </Box>
      </Box>

      <EquipmentTable
        selectEquipment={selectEquipment}
        setSelectEquipment={setSelectEquipment}
        setDrawerEdit={setDrawerEdit}
        setSelectedEquipamentItem={setSelectedEquipamentItem}
        selectEquipamentItem={selectEquipamentItem as EquipmentItem}
        setDrawerTransfer={setDrawerTransfer}
        equipmentData={equipment}
      />
      <LateralDrawer open={drawerEdit} setOpen={setDrawerEdit} position="right" size="bigger">
        <NewEditEquipmentDetails selectItem={selectEquipamentItem as EquipmentItem} setDrawerOpen={setDrawerEdit} />
      </LateralDrawer>
      <LateralDrawer open={drawerTransfer} setOpen={setDrawerTransfer} position="right" size="bigger">
        <NewEditTransferCost selectedItem={selectedItem as TransferDetailBase} setDrawerOpen={setDrawerTransfer} />
      </LateralDrawer>
    </Box>
  );
}

export default Equipment;
